import { useRef, useContext, useEffect, useState } from 'react';
import qs from 'qs';
import styled from '@emotion/styled';
import { ui } from '@owenscorning/pcb.alpha';

import Cta from './Cta';
import { H2, H4, H5 } from '../oc-h';
import Text from './utilities/Text';
import { interpolateString } from '../../PageBuilder/helpers/interpolate';
import { connectLocation } from '../../location/LocationConnectors';
import mdms_api from '../../../mdms_api';
import Picture from '../oc-picture';
import Image from '../oc-image';
import Stars from '../../Stars';
import { profileURL } from '../../FindAContractor/profileURL';
import { Screens } from '../../PageBuilder/editing/Viewport';
import { openComponentModal } from '../../PageBuilder/helpers/modal';
import { SchemaForm } from '../../forms/InnerSchemaForm';


const ContractorListWrapper = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-start;
  margin-top: 10px;

  > div {
    display: flex;
    flex-basis: calc((100% / 3));
    gap: 16px;
    max-width: 100%;

    .icons-container {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;

      .ratings {
        .star-icon {
          width: 14px;
        }
      }
    }

    .contact-info {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;

      span:not(:first-child) {
        ::before {
          content: " | "
        }
      }

      .phone {
        text-decoration: none;
      }
    }

    picture {
      flex-shrink: 0;

      img {
        max-width: 72px;
      }
    }
  }

  @media (max-width: ${Screens.Tablet}px) {
    flex-direction: column;
  }
`

const RequestAQuoteModal = ({ code, t, contractor, zip }) => {
  let params = {};

  if (contractor) {
    params = {
      initialWidth: 0,
      parentTitle: typeof document !== 'undefined' ? document.title : '',
      parentUrl: typeof document !== 'undefined' ? document.location.href : '',
      zipcode: zip,
      contractor: contractor.membership_number,
      contractor_mas_dealer_code: contractor.membership_number,
      source: 'mini-fac',
      'meta[data-track-contractor-id]': contractor.membership_number,
      'meta[data-track-contractor]': contractor.name,
      lang: code,
      contractor_store_name: contractor.name,
    };
  }

  const query = `?${qs.stringify(params)}`;

  return (
    <div>
      <SchemaForm
        uid={`www.owenscorning.com/${(code || 'en-US').toLowerCase()}/roofing/request-a-quote-form`}
        search={query}
      />
    </div>
  );
}

// Platinum contractor condition is taken from contractor_data_helper.rb where it specifies that
// { is_preferred_platinum: data[:tier_level] == 3 }
const contractorRender = (contractor, t, locale, zip) => {
  const phoneDigits = contractor.phone?.replace(/[^0-9A-Z]/g, '');
  const formattedPhone = phoneDigits?.replace(/(.{3})(.{3})(.{4,})/, '($1) $2-$3');
  const modalContractor = { ...contractor, mas_dealer_code: contractor.membership_number, store_name: contractor.name }
  const defaultLogo = 'https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto,w_65/v1589224258/roofing/contractors/house-generic.png';

  return(
    <div key={`mini-fac-${contractor.id}`}>
      <Picture source={ contractor.logo || defaultLogo } alt={ contractor.name } transform={{ width: 72, height: 72 }}/>
      <div>
        <H4>
          <Text content={contractor.name}/>
        </H4>
        <p className="icons-container">
          { contractor.tier_level === 3 &&
            <Image src={t('contractor_profile.platinum_icon')} alt={t('contractor_profile.platinum_title')} transform={{ width: 100 }}/>
          }
          { contractor.gq_review_count > 0 &&
            <span className="ratings">
              <Stars stars={contractor.gq_review_stars} />
              &nbsp;
              { t('contractor_profile.review_count', { count: contractor.gq_review_count }) }
            </span>
          }
        </p>
        <p className="contact-info">
          { formattedPhone &&
            <a
              href={`tel:${phoneDigits}`}
              className="phone"
              data-track="phone"
              data-track-professional="contractor"
              data-track-contractor-id={contractor.membership_number}
              data-track-contractor={contractor.name}
            >
              {formattedPhone}
            </a>
          }
          { contractor.website &&
            <span>
              <a
                href={contractor.website}
                data-track="website"
                data-track-professional="contractor"
                data-track-contractor-id={contractor.membership_number}
                data-track-contractor={contractor.name}
              >
                Website
            </a>
            </span>
          }
          <span>
            <a
              href="#"
              onClick={e => {
                openComponentModal(() => <RequestAQuoteModal code={locale.code} zip={zip} t={t} contractor={contractor}/>)
              }}
              data-track="form-start"
              data-track-professional="contractor"
              data-track-form-name = "request-a-quote"
              data-track-contractor-id={contractor.membership_number}
              data-track-contractor={contractor.name}
            >
              Request Quote
            </a>
          </span>
        </p>
      </div>
    </div>
)};


const contractorListRender = (contractors, t, locale, zip, notFoundCopy, loading) => {
  if (loading) {
    return(
      <div>
        <UI.Icon type="spinner" spin large />
      </div>
    )
  }

  if (!contractors || contractors.length <= 0) {
    return(<div> { notFoundCopy } </div>)
  }

  return(
    <ContractorListWrapper>
      { contractors.map(contractor => (contractorRender(contractor, t, locale, zip))) }
    </ContractorListWrapper>
  )

}

const findAContractor = connectLocation(({
  heading = 'Find a contractor',
  bodyCopy = 'Get your roof installed by a contractor near {city}, {region}',
  ctaLabel = 'View More Contractors',
  ctaLink = 'https://www.owenscorning.com/roofing/contractors',
  notFoundCopy = "We're sorry, we did not find any contractors in your area.",
  address,
  t,
  locale,
}) => {
  const CtaButtonRef = useRef(null);
  const { city, region, latitude, longitude, country_code, zip_code } = address;
  const interpolatedBodyCopy = interpolateString(bodyCopy, { city, region });
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(async () => {
    if (!latitude || !longitude) {
      setContractors([]);
      return;
    }

    const query = qs.stringify(
      {
        lat: latitude,
        lng: longitude,
        per_page: 3,
        radius: 20,
        sort: 'default',
        units: country_code === 'US' ? 'mi' : 'km',
        c_code: country_code,
      },
      { arrayFormat: 'brackets' },
    );
    const url = `/api/v3/locations/roofing-contractors?${query}`;
    const response = await mdms_api.get(url);
    setLoading(false);
    if (response.status === 200) {
      await response.json().then(json => setContractors(json.data))
      return;
    } else if (response.status === 204) {
      setContractors([])
      return null;
    }
  }, [latitude, longitude]);

  return (
    <div>
      <H2 tag="h2" font="body">
        <Text content={heading} />
      </H2>
      <H5> <Text content={interpolatedBodyCopy} /> </H5>
      { contractorListRender(contractors, t, locale, zip_code, notFoundCopy, loading) }
      <Cta
        color='white'
        url={ ctaLink }
        text={ ctaLabel }
        forwardRef={CtaButtonRef}
        analytics={{ 'track': 'learn-more', 'track-destination': ctaLink }}
      />
    </div>
  );
});

export default findAContractor
